import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { OutlinedBadge, type BadgeColor } from '@tms/shared-ui';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import {
  deletePlayback,
  listPlaybacks,
  updatePlayback,
  type PlaybackRow,
} from '../services/playbackApi';
import { ContentPageLayout } from '../components/Shared';
import { useLanguage } from '../hooks/useLanguage';
import { useTranslation } from 'react-i18next';

const STATUSES = ['draft', 'published', 'archived'] as const;
const NO_CHANGE = '';
type BulkTagsMode = 'none' | 'replace' | 'append';
type PlaybackStatus = (typeof STATUSES)[number];

const parseTags = (input: string) =>
  input.split(',').map((tag) => tag.trim()).filter(Boolean);

const listMissingAudioParts = (playback: PlaybackRow | undefined) => {
  const parts = playback?.playbackParts || [];
  return parts
    .map((part, index) => ({
      name: part.partName?.trim() || `Part ${index + 1}`,
      missing:
        (part.audioTempos || []).length === 0
        || (part.audioTempos || []).some((tempo) => !String(tempo.audioFile || '').trim()),
    }))
    .filter((part) => part.missing)
    .map((part) => part.name);
};

const playbackHasMissingAudio = (playback: PlaybackRow | undefined) => {
  if (!playback?.playbackParts?.length) return true;
  return listMissingAudioParts(playback).length > 0;
};

const describeBlockedPlayback = (playback: PlaybackRow | undefined) => {
  const title = playback?.title || playback?.code || 'playback';
  const parts = listMissingAudioParts(playback);
  return parts.length ? `${title} (${parts.join(', ')})` : title;
};

const normalizeStatus = (status: string | undefined): PlaybackStatus => {
  if (status === 'published' || status === 'archived') return status;
  return 'draft';
};

const PlaybacksAdminPage: React.FC = () => {
  const { navigateWithLang } = useLanguage();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [publishAlert, setPublishAlert] = useState<string | null>(null);
  const [playbacks, setPlaybacks] = useState<PlaybackRow[]>([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | PlaybackStatus>('all');
  const [audioMissingOnly, setAudioMissingOnly] = useState(false);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkStatus, setBulkStatus] = useState(NO_CHANGE);
  const [bulkIsPublic, setBulkIsPublic] = useState(NO_CHANGE);
  const [bulkTagsMode, setBulkTagsMode] = useState<BulkTagsMode>('none');
  const [bulkTags, setBulkTags] = useState('');

  const statusLabel = (status: PlaybackStatus) => {
    if (status === 'published') return t('playbacksAdmin.statusPublished');
    if (status === 'archived') return t('playbacksAdmin.statusArchived');
    return t('playbacksAdmin.statusDraft');
  };

  const statusColor = (status: PlaybackStatus): BadgeColor => {
    if (status === 'published') return 'success';
    if (status === 'archived') return 'default';
    return 'default';
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listPlaybacks({ limit: 500, sortBy: 'lastModified', sortOrder: 'desc' });
      setPlaybacks(res.data?.playbacks || []);
    } catch (e: any) {
      setError(e?.message || t('playbacksAdmin.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const searched = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return playbacks;
    return playbacks.filter((playback) => (
      playback.title?.toLowerCase().includes(query)
      || playback.code?.toLowerCase().includes(query)
      || (playback.tags || []).some((tag) => tag.toLowerCase().includes(query))
    ));
  }, [playbacks, search]);

  const statusCounts = useMemo(() => ({
    all: searched.length,
    draft: searched.filter((playback) => normalizeStatus(playback.status) === 'draft').length,
    published: searched.filter((playback) => normalizeStatus(playback.status) === 'published').length,
    archived: searched.filter((playback) => normalizeStatus(playback.status) === 'archived').length,
    audioMissing: searched.filter((playback) => playbackHasMissingAudio(playback)).length,
  }), [searched]);

  const filtered = useMemo(
    () => searched.filter((playback) => {
      if (statusFilter !== 'all' && normalizeStatus(playback.status) !== statusFilter) return false;
      if (audioMissingOnly && !playbackHasMissingAudio(playback)) return false;
      return true;
    }),
    [searched, statusFilter, audioMissingOnly],
  );

  const playbackById = useMemo(() => new Map(playbacks.map((playback) => [playback._id, playback])), [playbacks]);
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const allOnPageSelected = filtered.length > 0 && filtered.every((playback) => selectedSet.has(playback._id));
  const someOnPageSelected = filtered.some((playback) => selectedSet.has(playback._id));

  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => playbacks.some((playback) => playback._id === id)));
  }, [playbacks]);

  const toggleSelectAllOnPage = () => {
    if (allOnPageSelected) {
      const pageIds = new Set(filtered.map((playback) => playback._id));
      setSelectedIds((prev) => prev.filter((id) => !pageIds.has(id)));
    } else {
      setSelectedIds([...new Set([...selectedIds, ...filtered.map((playback) => playback._id)])]);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]));
  };

  const clearSelection = () => setSelectedIds([]);

  const openBulkEdit = () => {
    setBulkStatus(NO_CHANGE);
    setBulkIsPublic(NO_CHANGE);
    setBulkTagsMode('none');
    setBulkTags('');
    setPublishAlert(null);
    setBulkEditOpen(true);
  };

  const openEdit = (playback: PlaybackRow) => {
    navigateWithLang(`/playbacks/${playback._id}`);
  };

  const openCreate = () => {
    navigateWithLang('/playbacks/new');
  };

  const formatUpdated = (value?: string) => {
    if (!value) return '—';
    return new Date(value).toLocaleDateString(i18n.language, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('playbacksAdmin.deleteConfirm'))) return;
    try {
      await deletePlayback(id);
      setSelectedIds((prev) => prev.filter((value) => value !== id));
      setMsg(t('playbacksAdmin.deleted'));
      await load();
    } catch (e: any) {
      setMsg(e?.message || t('playbacksAdmin.deleteFailed'));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(t('playbacksAdmin.bulkDeleteConfirm', { count: selectedIds.length }))) return;
    setBulkSaving(true);
    setMsg(null);
    const count = selectedIds.length;
    try {
      for (const id of selectedIds) {
        await deletePlayback(id);
      }
      setSelectedIds([]);
      setMsg(t('playbacksAdmin.bulkDeleted', { count }));
      await load();
    } catch (e: any) {
      setMsg(e?.message || t('playbacksAdmin.bulkDeleteFailed'));
    } finally {
      setBulkSaving(false);
    }
  };

  const handleBulkEditApply = async () => {
    const hasStatus = bulkStatus !== NO_CHANGE;
    const hasIsPublic = bulkIsPublic !== NO_CHANGE;
    const hasTags = bulkTagsMode !== 'none' && bulkTags.trim().length > 0;

    if (!hasStatus && !hasIsPublic && !hasTags) {
      setMsg(t('playbacksAdmin.chooseField'));
      return;
    }
    if (selectedIds.length === 0) return;

    if (hasStatus && bulkStatus === 'published') {
      const blocked = selectedIds
        .map((id) => playbackById.get(id))
        .filter((playback) => playbackHasMissingAudio(playback));
      if (blocked.length > 0) {
        setPublishAlert(t('playbackEditor.cannotPublishBulk', {
          titles: blocked.map((playback) => describeBlockedPlayback(playback)).join(', '),
        }));
        return;
      }
    }

    setBulkSaving(true);
    setMsg(null);
    setPublishAlert(null);
    try {
      let updated = 0;
      for (const id of selectedIds) {
        const body: Record<string, unknown> = {};
        if (hasStatus) body.status = bulkStatus;
        if (hasIsPublic) body.isPublic = bulkIsPublic === 'yes';
        if (hasTags) {
          if (bulkTagsMode === 'replace') {
            body.tags = parseTags(bulkTags);
          } else {
            const existing = playbackById.get(id)?.tags || [];
            body.tags = [...new Set([...existing, ...parseTags(bulkTags)])];
          }
        }
        if (Object.keys(body).length === 0) continue;
        await updatePlayback(id, body);
        updated += 1;
      }
      setBulkEditOpen(false);
      setSelectedIds([]);
      setMsg(t('playbacksAdmin.updated', { count: updated }));
      await load();
    } catch (e: any) {
      const missing = e?.data?.missingAudioParts;
      if (Array.isArray(missing) && missing.length > 0) {
        setPublishAlert(t('playbackEditor.cannotPublishNamed', { names: missing.join(', ') }));
      }
      setMsg(e?.message || t('playbacksAdmin.bulkEditFailed'));
    } finally {
      setBulkSaving(false);
    }
  };

  return (
    <ContentPageLayout>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={2.5}>
          <Box>
            <Typography variant="h5" color="primary" fontWeight="bold">{t('playbacksAdmin.title')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 560 }}>
              {t('playbacksAdmin.subtitle')}
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            {t('playbacksAdmin.create')}
          </Button>
        </Box>

        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        {msg ? <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg}</Alert> : null}

        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box display="flex" flexDirection="column" gap={1.5}>
            <TextField
              size="small"
              fullWidth
              placeholder={t('playbacksAdmin.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')} aria-label={t('playbacksAdmin.clearSelection')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
            <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
              <ToggleButtonGroup
                exclusive
                size="small"
                value={statusFilter}
                onChange={(_event, value: 'all' | PlaybackStatus | null) => {
                  if (value) setStatusFilter(value);
                }}
              >
                <ToggleButton value="all">{t('playbacksAdmin.statusAll')} · {statusCounts.all}</ToggleButton>
                <ToggleButton value="published">{t('playbacksAdmin.statusPublished')} · {statusCounts.published}</ToggleButton>
                <ToggleButton value="draft">{t('playbacksAdmin.statusDraft')} · {statusCounts.draft}</ToggleButton>
                <ToggleButton value="archived">{t('playbacksAdmin.statusArchived')} · {statusCounts.archived}</ToggleButton>
              </ToggleButtonGroup>
              <Chip
                size="small"
                variant={audioMissingOnly ? 'filled' : 'outlined'}
                color={audioMissingOnly ? 'secondary' : 'default'}
                label={`${t('playbacksAdmin.audioFilter')} · ${statusCounts.audioMissing}`}
                onClick={() => setAudioMissingOnly((prev) => !prev)}
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                {filtered.length === playbacks.length
                  ? t('playbacksAdmin.results', { count: filtered.length })
                  : t('playbacksAdmin.resultsFiltered', { shown: filtered.length, total: playbacks.length })}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {selectedIds.length > 0 ? (
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              mb: 2,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              alignItems: 'center',
              bgcolor: 'action.hover',
            }}
          >
            <Typography variant="body2" color="primary" sx={{ fontWeight: 600, mr: 1 }}>
              {t('playbacksAdmin.selected', { count: selectedIds.length })}
            </Typography>
            <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={openBulkEdit} disabled={bulkSaving}>
              {t('playbacksAdmin.bulkEdit')}
            </Button>
            <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleBulkDelete} disabled={bulkSaving}>
              {t('delete')}
            </Button>
            <Button size="small" onClick={clearSelection} disabled={bulkSaving}>
              {t('playbacksAdmin.clearSelection')}
            </Button>
          </Paper>
        ) : null}

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : filtered.length === 0 ? (
          <Paper variant="outlined" sx={{ py: 7, px: 3, textAlign: 'center' }}>
            <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
              {playbacks.length === 0 ? t('playbacksAdmin.emptyNone') : t('playbacksAdmin.empty')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('playbacksAdmin.emptyHint')}
            </Typography>
            {playbacks.length === 0 ? (
              <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                {t('playbacksAdmin.create')}
              </Button>
            ) : null}
          </Paper>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ width: 48 }}>
                    <Checkbox
                      size="small"
                      checked={allOnPageSelected}
                      indeterminate={someOnPageSelected && !allOnPageSelected}
                      onChange={toggleSelectAllOnPage}
                      inputProps={{ 'aria-label': t('playbacksAdmin.statusAll') }}
                    />
                  </TableCell>
                  <TableCell>{t('playbacksAdmin.colPlayback')}</TableCell>
                  <TableCell>{t('playbacksAdmin.colStatus')}</TableCell>
                  <TableCell>{t('playbacksAdmin.colAudio')}</TableCell>
                  <TableCell>{t('playbacksAdmin.colParts')}</TableCell>
                  <TableCell>{t('playbacksAdmin.colVisibility')}</TableCell>
                  <TableCell>{t('playbacksAdmin.colUpdated')}</TableCell>
                  <TableCell align="right">{t('actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((playback) => {
                  const status = normalizeStatus(playback.status);
                  const missingAudio = playbackHasMissingAudio(playback);
                  return (
                    <TableRow
                      key={playback._id}
                      selected={selectedSet.has(playback._id)}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => openEdit(playback)}
                    >
                      <TableCell padding="checkbox" onClick={(event) => event.stopPropagation()}>
                        <Checkbox
                          size="small"
                          checked={selectedSet.has(playback._id)}
                          onChange={() => toggleSelect(playback._id)}
                          inputProps={{ 'aria-label': playback.title || playback.code || 'playback' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {playback.title || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {playback.code || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <OutlinedBadge label={statusLabel(status)} color={statusColor(status)} />
                      </TableCell>
                      <TableCell>
                        <OutlinedBadge
                          label={missingAudio ? t('playbacksAdmin.audioMissing') : t('playbacksAdmin.audioReady')}
                          color={missingAudio ? 'secondary' : 'success'}
                        />
                      </TableCell>
                      <TableCell>{playback.playbackParts?.length ?? 0}</TableCell>
                      <TableCell>
                        <OutlinedBadge
                          label={playback.isPublic ? t('playbacksAdmin.public') : t('playbacksAdmin.private')}
                          color={playback.isPublic ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatUpdated(playback.lastModified || playback.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" onClick={(event) => event.stopPropagation()}>
                        <Tooltip title={t('playbacksAdmin.editTooltip')}>
                          <IconButton size="small" onClick={() => openEdit(playback)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('playbacksAdmin.deleteTooltip')}>
                          <IconButton size="small" color="error" onClick={() => handleDelete(playback._id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Dialog open={bulkEditOpen} onClose={() => !bulkSaving && setBulkEditOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{t('playbacksAdmin.bulkTitle', { count: selectedIds.length })}</DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
              {publishAlert ? <Alert severity="error">{publishAlert}</Alert> : null}
              <FormControl size="small" fullWidth>
                <InputLabel>{t('playbacksAdmin.colStatus')}</InputLabel>
                <Select
                  label={t('playbacksAdmin.colStatus')}
                  value={bulkStatus}
                  onChange={(e) => {
                    setBulkStatus(e.target.value);
                    setPublishAlert(null);
                  }}
                >
                  <MenuItem value={NO_CHANGE}>{t('playbacksAdmin.noChange')}</MenuItem>
                  {STATUSES.map((status) => (
                    <MenuItem key={status} value={status}>{statusLabel(status)}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel>{t('playbacksAdmin.colVisibility')}</InputLabel>
                <Select
                  label={t('playbacksAdmin.colVisibility')}
                  value={bulkIsPublic}
                  onChange={(e) => setBulkIsPublic(e.target.value)}
                >
                  <MenuItem value={NO_CHANGE}>{t('playbacksAdmin.noChange')}</MenuItem>
                  <MenuItem value="yes">{t('playbacksAdmin.public')}</MenuItem>
                  <MenuItem value="no">{t('playbacksAdmin.private')}</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel>{t('playbacksAdmin.appendTags')}</InputLabel>
                <Select
                  label={t('playbacksAdmin.appendTags')}
                  value={bulkTagsMode}
                  onChange={(e) => setBulkTagsMode(e.target.value as BulkTagsMode)}
                >
                  <MenuItem value="none">{t('playbacksAdmin.noChange')}</MenuItem>
                  <MenuItem value="replace">{t('playbacksAdmin.replaceTags')}</MenuItem>
                  <MenuItem value="append">{t('playbacksAdmin.appendTags')}</MenuItem>
                </Select>
              </FormControl>

              {bulkTagsMode !== 'none' ? (
                <TextField
                  size="small"
                  fullWidth
                  label={bulkTagsMode === 'replace' ? t('playbacksAdmin.newTags') : t('playbacksAdmin.addTags')}
                  value={bulkTags}
                  onChange={(e) => setBulkTags(e.target.value)}
                  placeholder={t('playbacksAdmin.tagsHint')}
                />
              ) : null}

              <Typography variant="caption" color="text.secondary">
                {t('playbacksAdmin.bulkHint')}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBulkEditOpen(false)} disabled={bulkSaving}>{t('cancel')}</Button>
            <Button variant="contained" onClick={handleBulkEditApply} disabled={bulkSaving}>
              {bulkSaving ? t('playbacksAdmin.applying') : t('playbacksAdmin.apply')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ContentPageLayout>
  );
};

export default PlaybacksAdminPage;
